import { Component, Input, OnInit } from "@angular/core";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { IAdaptMenuItem } from "@common/ux/menu/menu.component";
import isEqual from "lodash.isequal";
import { EMPTY, merge } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { ColumnSizePresetGroups, ILayoutBase } from "../layout.interface";
import { LayoutManagerComponent } from "../layout-manager/layout-manager.component";

@Component({
    selector: "adapt-layout-manager-controls",
    templateUrl: "./layout-manager-controls.component.html",
    styleUrls: ["./layout-manager-controls.component.scss"],
    standalone: false,
})
export class LayoutManagerControlsComponent extends BaseComponent implements OnInit {
    @Input({ required: true }) public layoutManager!: LayoutManagerComponent;

    public saveError?: string;
    public menuItems!: IAdaptMenuItem[];

    public ngOnInit() {
        merge(
            this.layoutManager.initialised,
            this.layoutManager.layoutUpdated$,
            this.layoutManager.isEditing$,
        ).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => this.updateMenu());

        // clear error when entering/exiting edit mode
        this.layoutManager.isEditing$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => this.saveError = undefined);
    }

    @Autobind
    public saveLayout() {
        this.saveError = undefined;
        return this.layoutManager.saveLayout().pipe(
            catchError((e) => {
                this.saveError = ErrorHandlingUtilities.getHttpResponseMessage(e);
                return EMPTY;
            }),
            switchMap(() => this.layoutManager.toggleEditing(false)),
        );
    }

    private getColumnSizes(layout?: ILayoutBase) {
        if (!layout) {
            return [];
        }
        return layout.columns.filter((col) => col.options.enabled)
            .map((col) => col.size ?? "");
    }

    private updateMenu() {
        const enabledColumnSizes = this.getColumnSizes(this.layoutManager.currentLayout);

        const generateMenuItem = (text: string, sizes: string[]) => {
            return {
                text,
                icon: "",
                onClick: () => this.layoutManager.resizeLayout(sizes).subscribe(),
                isChecked: isEqual(enabledColumnSizes, sizes),
            } as IAdaptMenuItem;
        };

        const items: IAdaptMenuItem[] = [];

        for (const [text, columnSizes] of Object.entries(ColumnSizePresetGroups)) {
            // ungrouped, no children
            if (text === "") {
                for (const columnSize of columnSizes) {
                    const preset = this.layoutManager.columnSizePresets[columnSize];
                    if (preset) {
                        items.push(generateMenuItem(columnSize, preset));
                    }
                }
            } else {
                const subMenu = {
                    text: `${text}...`,
                    items: [],
                } as IAdaptMenuItem;

                for (const columnSize of columnSizes) {
                    const preset = this.layoutManager.columnSizePresets[columnSize];
                    if (preset) {
                        subMenu.items!.push(generateMenuItem(columnSize, preset));
                    }
                }

                if (subMenu.items?.length) {
                    items.push(subMenu);
                }
            }
        }

        this.menuItems = [{
            text: "Page columns",
            icon: "fal fa-columns",
            items,
        }];
    }
}
